/**
 * One agent's card (audits/2026-10-26/agent-control-spec.md, rev. 2): status, limits, levels and
 * scope, for a platform admin. Every section says what it does and on what condition
 * (`lib/agent-controls-copy.ts`). A service client is bound here, by its subject, through the
 * kit's agents route (PATCH /agents/:id); the subject is read from the kit's agents list.
 *
 * The one-screen layout (audits/2026-10-29/gui/plan.md, PR 7): the sections are panels side by
 * side. Limits and Access carry their Save in the header; Status writes on change. The words and
 * the controls are the ones the sections had; only where they sit has changed.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
	CARD_ABOUT,
	LEVELS_HELP,
	LIMITS_HELP,
	SCOPE_HELP,
	STATUS_TODAY,
	SUBJECT_HELP,
	SUBJECT_LABEL,
	SUBJECT_NONE,
	SUBJECT_NOT_READ,
	UNAVAILABLE,
	campaignIdsOf,
	capText,
	platformCapWords,
	subjectAnswerText,
	subjectBody,
} from "../lib/agent-controls-copy.ts";
import { ADVISOR_ONLY_ONE, kindChangeBody, kindOptions } from "../lib/agent-kinds.ts";
import { type ActionLevel, type McpCallRow, type Standing, api } from "../lib/api.ts";
import {
	MCP_CALLS_ABOUT,
	MCP_CALLS_NONE,
	MCP_CALLS_NOT_READ,
	MCP_CALLS_SHOWN,
	MCP_CALLS_TITLE,
	MCP_CALLS_UNAVAILABLE,
	MCP_STATUS_HELP,
	callStatus,
	callTime,
	callsShownLine,
} from "../lib/mcp-calls-copy.ts";
import { amountToMicro } from "../lib/money.ts";
import { isPlatformAdmin } from "../lib/roles.ts";
import { useLoad } from "../lib/useLoad.ts";
import {
	Button,
	InfoBanner,
	Input,
	Page,
	Panel,
	Select,
	Table,
	type TableColumn,
} from "../ui/index.ts";

const STATUSES = [
	{ value: "active", label: "Active" },
	{ value: "paused", label: "Paused" },
	{ value: "retired", label: "Retired" },
];
const LEVEL_OPTIONS = [
	{ value: "self", label: "self" },
	{ value: "propose", label: "propose" },
	{ value: "never", label: "never" },
];

type ScopeDraft = { xAccountId: string; permission: "read" | "write"; campaigns: string };

const CALL_COLUMNS: TableColumn<McpCallRow>[] = [
	{ id: "at", header: "When", accessor: (r) => callTime(r.at) },
	{ id: "tool", header: "Tool", accessor: (r) => r.tool },
	{ id: "status", header: "Status", accessor: (r) => callStatus(r) },
	{ id: "ms", header: "ms", accessor: (r) => r.ms },
	{ id: "request", header: "Request id", accessor: (r) => r.requestId ?? "—", wrap: true },
];

export function AgentCard({ standing }: { standing: Standing }) {
	const { id = "" } = useParams();
	const [controls, reload] = useLoad(() => api.agentControls(id));
	const [accounts] = useLoad(() => api.xAccounts());
	const [calls] = useLoad(() => api.agentMcpCalls(id, MCP_CALLS_SHOWN));
	// The subject is not in the controls answer: the kit's agents list carries it, for a
	// platform admin only (it is null for anyone else).
	const [agents, reloadAgents] = useLoad(() => api.agents());
	const agentRow =
		agents.state === "ready" ? agents.data.agents.find((a) => a.id === id) : undefined;
	const [subject, setSubject] = useState("");
	useEffect(() => {
		setSubject(agentRow?.subject ?? "");
	}, [agentRow?.subject]);
	const [note, setNote] = useState<{ text: string; failed: boolean } | null>(null);
	const [busy, setBusy] = useState(false);

	const data = controls.state === "ready" ? controls.data : null;
	const [day, setDay] = useState("");
	const [total, setTotal] = useState("");
	const [levels, setLevels] = useState<Record<string, ActionLevel>>({});
	const [scope, setScope] = useState<ScopeDraft[]>([]);
	useEffect(() => {
		if (!data) return;
		setDay(capText(data.policy?.capDayMicro ?? null));
		setTotal(capText(data.policy?.capTotalMicro ?? null));
		setLevels(
			data.policy?.levels ?? Object.fromEntries(data.actions.map((a) => [a.key, a.default])),
		);
		setScope(
			(data.scope ?? []).map((s) => ({
				xAccountId: s.xAccountId,
				permission: s.permission,
				campaigns: (s.campaignIds ?? []).join(", "),
			})),
		);
	}, [data]);

	if (!isPlatformAdmin(standing)) {
		return (
			<Page title="Agent" description={CARD_ABOUT}>
				<InfoBanner variant="info" title="Only a platform admin sets an agent's controls." />
			</Page>
		);
	}

	async function act(what: () => Promise<{ status: number; body: unknown }>, done: string) {
		setBusy(true);
		setNote(null);
		try {
			const a = await what();
			const error = (a.body as { error?: string } | null)?.error;
			setNote(
				a.status >= 400
					? { text: error ?? `HTTP ${a.status}`, failed: true }
					: { text: done, failed: false },
			);
			reload();
		} catch (e) {
			setNote({ text: e instanceof Error ? e.message : String(e), failed: true });
		} finally {
			setBusy(false);
		}
	}

	/** A field in euros as micro units; empty is null («not configured»); anything else refuses. */
	const figure = (text: string): number | null | "bad" => {
		if (text.trim() === "") return null;
		return amountToMicro(text) ?? "bad";
	};

	function savePolicy() {
		const d = figure(day);
		const t = figure(total);
		if (d === "bad" || t === "bad") {
			setNote({
				text: "A limit is an amount in euros, like 25 or 25.50 — or empty.",
				failed: true,
			});
			return;
		}
		const changed = Object.fromEntries(
			Object.entries(levels).filter(
				([k, v]) => data?.actions.find((a) => a.key === k)?.default !== v,
			),
		);
		void act(
			() => api.setAgentPolicy(id, { capDayMicro: d, capTotalMicro: t, levels: changed }),
			"Limits and levels saved.",
		);
	}

	async function saveSubject() {
		setBusy(true);
		setNote(null);
		try {
			const a = await api.setAgentSubject(id, subjectBody(subject));
			setNote(subjectAnswerText(a.status, a.body));
			reloadAgents();
		} catch (e) {
			setNote({ text: e instanceof Error ? e.message : String(e), failed: true });
		} finally {
			setBusy(false);
		}
	}

	function saveScope() {
		void act(
			() =>
				api.setAgentScope(id, {
					accounts: scope.map((s) => ({
						xAccountId: s.xAccountId,
						permission: s.permission,
						campaignIds: campaignIdsOf(s.campaigns),
					})),
				}),
			"Access saved.",
		);
	}

	const accountList = accounts.state === "ready" ? accounts.data.accounts : [];

	return (
		<Page fill title={data ? data.agent.name : "Agent"} description={CARD_ABOUT}>
			<div className="flex h-full min-h-0 flex-col gap-3">
				{note && <InfoBanner variant={note.failed ? "danger" : "info"} title={note.text} />}
				{controls.state === "failed" && (
					<InfoBanner variant="danger" title="The agent could not be read.">
						{controls.why}
					</InfoBanner>
				)}
				{data?.unavailable && (
					<InfoBanner variant="warning" title={UNAVAILABLE}>
						{data.unavailable}
					</InfoBanner>
				)}

				{data && (
					<div className="grid min-h-0 flex-1 grid-cols-3 gap-3">
						<Panel title="Status">
							<Select
								label="Status"
								value={data.agent.status}
								disabled={busy}
								onChange={(e) =>
									void act(async () => {
										await api.updateAgent(id, { status: e.target.value });
										return { status: 200, body: {} };
									}, "Status saved.")
								}
								options={STATUSES}
							/>
							<p className="text-sm text-muted">{STATUS_TODAY}</p>
							<Select
								label="Kind"
								value={data.agent.kind}
								disabled={busy}
								onChange={(e) =>
									void act(async () => {
										await api.updateAgent(id, kindChangeBody(e.target.value));
										return { status: 200, body: {} };
									}, "Kind saved.")
								}
								options={kindOptions(data.agent.kind)}
							/>
							<p className="text-xs text-muted">{ADVISOR_ONLY_ONE}</p>
							<h3 className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted">
								{SUBJECT_LABEL}
							</h3>
							<p className="text-xs text-muted">{SUBJECT_HELP}</p>
							{agents.state === "failed" && (
								<InfoBanner variant="danger" title="The agents list could not be read.">
									{agents.why}
								</InfoBanner>
							)}
							{agents.state === "ready" && !agentRow && (
								<p className="text-sm text-muted">{SUBJECT_NOT_READ}</p>
							)}
							{agentRow && (
								<>
									<p className="text-sm text-content">
										{agentRow.subject ? agentRow.subject : SUBJECT_NONE}
									</p>
									<Input
										label={SUBJECT_LABEL}
										value={subject}
										onChange={(e) => setSubject(e.target.value)}
									/>
									<div>
										<Button size="sm" onClick={() => void saveSubject()} loading={busy}>
											Save subject
										</Button>
									</div>
								</>
							)}
						</Panel>

						{!data.unavailable && (
							<>
								<Panel
									title="Limits"
									actions={
										<Button size="sm" onClick={savePolicy} loading={busy}>
											Save limits and levels
										</Button>
									}
								>
									<p className="text-sm text-muted">{LIMITS_HELP}</p>
									<p className="text-xs text-muted">{platformCapWords(data.platformCap)}</p>
									<Input label="Daily, €" value={day} onChange={(e) => setDay(e.target.value)} />
									<Input
										label="Total, €"
										value={total}
										onChange={(e) => setTotal(e.target.value)}
									/>
									<h3 className="pt-2 text-xs font-semibold uppercase tracking-wider text-muted">
										Levels
									</h3>
									<p className="text-sm text-muted">{LEVELS_HELP}</p>
									{data.actions.map((a) => (
										<Select
											key={a.key}
											label={a.label}
											value={a.locked ? "never" : (levels[a.key] ?? a.default)}
											disabled={a.locked || busy}
											onChange={(e) =>
												setLevels((prev) => ({ ...prev, [a.key]: e.target.value as ActionLevel }))
											}
											options={LEVEL_OPTIONS}
										/>
									))}
								</Panel>

								<Panel
									title="Access"
									actions={
										<Button size="sm" onClick={saveScope} loading={busy}>
											Save access
										</Button>
									}
								>
									<p className="text-sm text-muted">{SCOPE_HELP}</p>
									{scope.map((s, i) => (
										<div
											key={s.xAccountId}
											className="grid gap-2 rounded-md border border-border p-2"
										>
											<span className="text-sm text-content">{s.xAccountId}</span>
											<Select
												label="Access"
												value={s.permission}
												onChange={(e) =>
													setScope((prev) =>
														prev.map((x, j) =>
															j === i
																? { ...x, permission: e.target.value as "read" | "write" }
																: x,
														),
													)
												}
												options={[
													{ value: "read", label: "read" },
													{ value: "write", label: "write" },
												]}
											/>
											<Input
												label="Campaign ids (empty: every campaign)"
												value={s.campaigns}
												onChange={(e) =>
													setScope((prev) =>
														prev.map((x, j) => (j === i ? { ...x, campaigns: e.target.value } : x)),
													)
												}
											/>
											<div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => setScope((prev) => prev.filter((_, j) => j !== i))}
												>
													Remove
												</Button>
											</div>
										</div>
									))}
									<Select
										label="Add an account"
										value=""
										placeholder="Choose an ad account"
										onChange={(e) => {
											const x = e.target.value;
											if (x && !scope.some((s) => s.xAccountId === x))
												setScope((prev) => [
													...prev,
													{ xAccountId: x, permission: "read", campaigns: "" },
												]);
										}}
										options={accountList.map((a) => ({
											value: a.xAccountId,
											label: `${a.name} · ${a.xAccountId}`,
										}))}
									/>
								</Panel>
							</>
						)}

						<Panel title={MCP_CALLS_TITLE} className="col-span-3">
							{calls.state === "failed" && (
								<InfoBanner variant="danger" title={MCP_CALLS_NOT_READ}>
									{calls.why}
								</InfoBanner>
							)}
							{calls.state === "ready" && calls.data.unavailable && (
								<InfoBanner variant="warning" title={MCP_CALLS_UNAVAILABLE}>
									{calls.data.unavailable}
								</InfoBanner>
							)}
							{calls.state === "ready" && !calls.data.unavailable && (
								<>
									<p className="text-sm text-muted">{MCP_CALLS_ABOUT}</p>
									<p className="text-xs text-muted">{MCP_STATUS_HELP}</p>
									{calls.data.calls.length > 0 && (
										<p className="text-xs text-muted">{callsShownLine(calls.data.calls.length)}</p>
									)}
									<Table
										dense
										columns={CALL_COLUMNS}
										data={calls.data.calls}
										keyExtractor={(r) => r.id}
										emptyMessage={MCP_CALLS_NONE}
									/>
								</>
							)}
						</Panel>
					</div>
				)}
			</div>
		</Page>
	);
}
